import React, { useState } from 'react';
import {
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  TextField,
  Typography,
} from '@mui/material';
import ConfirmDestructiveActionDialog from '../W86Components/ConfirmDestructiveActionDialog';
import {
  BillingModalFactList,
  BillingModalLead,
  BillingModalSection,
} from './BillingModalSections';
import {
  formatBillingDollars,
  formatDollarsFromCents,
  pricePer1kCreditsDisplay,
} from './billingFormatters';
import './billing.css';

export function EnableOverageModal({
  open,
  onClose,
  onConfirm,
  defaultSpendLimitCents = 5000,
  chargeIncrementCents = 2500,
  microDollarsPerCredit = 1000,
  loading,
  error,
}) {
  const [spendLimitDollars, setSpendLimitDollars] = useState(defaultSpendLimitCents / 100);
  const [checked, setChecked] = useState(false);

  React.useEffect(() => {
    if (open) {
      setSpendLimitDollars(defaultSpendLimitCents / 100);
      setChecked(false);
    }
  }, [open, defaultSpendLimitCents]);

  const priceDisplay = pricePer1kCreditsDisplay(microDollarsPerCredit);
  const incrementDollars = chargeIncrementCents / 100;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Enable on-demand usage?</DialogTitle>
      <DialogContent>
        <BillingModalLead>
          When your included credits are used up, Workflow86 keeps running and bills you
          automatically for additional credits.
        </BillingModalLead>

        <BillingModalSection title="How billing works">
          <BillingModalFactList
            items={[
              { label: 'Rate', value: `${priceDisplay} per 1,000 credits` },
              {
                label: 'Charges',
                value: `Every ${formatBillingDollars(incrementDollars)} of usage`,
              },
              { label: 'Spending limit', value: 'Required' },
            ]}
          />
        </BillingModalSection>

        <BillingModalSection title="Suggested limit" highlight>
          <BillingModalFactList
            items={[
              {
                label: 'Monthly cap',
                value: `${formatBillingDollars(defaultSpendLimitCents / 100)}/month`,
              },
            ]}
          />
        </BillingModalSection>

        <div className="billing-spend-stepper">
          <Typography className="billing-label">Monthly spending limit: $</Typography>
          <TextField
            size="small"
            type="number"
            inputProps={{ min: 5, max: 10000, step: 1 }}
            value={spendLimitDollars}
            onChange={(e) => setSpendLimitDollars(Number(e.target.value))}
            className="org-styled-textfield"
          />
        </div>

        <FormControlLabel
          className="billing-checkbox-row"
          control={<Checkbox checked={checked} onChange={(e) => setChecked(e.target.checked)} />}
          label="I understand I'll be charged automatically when I exceed my included credits."
        />

        {error ? (
          <Typography color="error" variant="body2" className="billing-inline-error">
            {error}
          </Typography>
        ) : null}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} disabled={loading} sx={{ textTransform: 'none' }}>
          Cancel
        </Button>
        <Button
          variant="contained"
          disabled={!checked || spendLimitDollars < 5 || loading}
          onClick={() =>
            onConfirm({ enabled: true, spendLimitCents: Math.round(spendLimitDollars * 100) })
          }
          sx={{ textTransform: 'none' }}
        >
          {loading ? 'Enabling…' : 'Enable on-demand usage'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export function EditOverageLimitModal({ open, onClose, onConfirm, currentLimitCents, loading, error }) {
  const [spendLimitDollars, setSpendLimitDollars] = useState((currentLimitCents || 5000) / 100);

  React.useEffect(() => {
    if (open) setSpendLimitDollars((currentLimitCents || 5000) / 100);
  }, [open, currentLimitCents]);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Edit spending limit</DialogTitle>
      <DialogContent>
        <div className="billing-spend-stepper">
          <Typography className="billing-label">Monthly spending limit: $</Typography>
          <TextField
            size="small"
            type="number"
            inputProps={{ min: 5, max: 10000, step: 1 }}
            value={spendLimitDollars}
            onChange={(e) => setSpendLimitDollars(Number(e.target.value))}
            className="org-styled-textfield"
          />
        </div>
        {error ? (
          <Typography color="error" variant="body2" className="billing-inline-error">
            {error}
          </Typography>
        ) : null}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} disabled={loading} sx={{ textTransform: 'none' }}>
          Cancel
        </Button>
        <Button
          variant="contained"
          disabled={spendLimitDollars < 5 || loading}
          onClick={() =>
            onConfirm({ enabled: true, spendLimitCents: Math.round(spendLimitDollars * 100) })
          }
          sx={{ textTransform: 'none' }}
        >
          {loading ? 'Saving…' : 'Save limit'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export function DisableOverageModal({ open, onClose, onConfirm, overageUnbilledCents, loading }) {
  return (
    <ConfirmDestructiveActionDialog
      open={open}
      onClose={onClose}
      onConfirm={onConfirm}
      title="Turn off on-demand usage?"
      description={`Usage will stop when your included credits run out.\n\nAny unbilled on-demand usage (${formatDollarsFromCents(overageUnbilledCents)}) will still be charged to your card on file.`}
      confirmLabel="Turn off"
      cancelLabel="Keep on-demand usage"
      loading={loading}
    />
  );
}

